import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {ContactDetailsType, IContactDto} from '../Contact.model'
import { Link } from 'react-router-dom'
import { Spinner } from '../../../common/component/Spinner'
import { useContactGetByIdMutation } from '../Contact.api'

interface Props {
  dataReload?: boolean
  data: IContactDto
  primaryLabel?: string
}

export const ContactPrimarySelectorListItem: FC<Props> = ({
  dataReload,
  data,
  primaryLabel,
}: Props) => {
  const intl = useIntl()

  const didFetchData = useRef(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isReloaded, setIsReloaded] = useState(false)
  const [contact, setContact] = useState<IContactDto>()

  const [getById] = useContactGetByIdMutation()

  useEffect(() => {
    const fetchData = async (data: IContactDto) => {
      try {
        setLoadError(undefined)
        if (!dataReload || !data.id) {
          setContact(data)
          return
        }

        if (!didFetchData.current) {
          const response: any = await getById({id: data.id})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
            setContact(data)
          } else {
            setContact({
              ...response.data,
              primary: data.primary,
            })
            setIsReloaded(true)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
        setContact(data)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData(data)
  }, [])

  const buildMainLine = (contact: IContactDto): string => {
    let description = ''
    if (contact.email && contact.telephone) {
      description += `${contact.email} | ${contact.telephone}`
    } else if (contact.email) {
      description += `${contact.email}`
    } else if (contact.telephone) {
      description += `${contact.telephone}`
    }

    if (!description && contact.details) {
      const emails = contact.details.filter((detail) => detail.type === ContactDetailsType.EMAIL)
      const telephones = contact.details.filter(
        (detail) => detail.type === ContactDetailsType.TELEPHONE
      )
      if (emails && emails.length > 0 && telephones && telephones.length > 0) {
        const email: string = emails[0].value ?? ''
        const telephone: string = telephones[0].value ?? ''
        description += `${email} | ${telephone}`
      } else if (emails && emails.length > 0) {
        description += `${emails[0].value ?? ''}`
      } else if (telephones && telephones.length > 0) {
        description += `${telephones[0].value ?? ''}`
      }
    }

    if (contact.address && contact.countryCode) {
      return description += description 
        ? ` | ${contact.address} | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}` 
        : `${contact.address} | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`
    } else if (contact.address) {
      return description += description 
        ? ` | ${contact.address}` 
        : `${contact.address}`
    } else if (contact.countryCode) {
      return (description += description
        ? ` | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`
        : `${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`)
    }
    return description.toUpperCase()
  }

  return (
    <>
      {isLoading ? (
        <div className='d-flex justify-content-start align-items-center'>
          <Spinner heightClass='h-50px' />
        </div>
      ) : (
        contact && (
          <div className='d-flex flex-stack'>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-35px symbol-circle mx-2'>
                <span className='symbol-label bg-secondary text-light-secondary fs-6 fw-bolder'>
                  {data.displayName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    {isReloaded ? (
                      <Link to={`/contacts/${contact.id}`} target='_blank'>
                        <div className='lh-2 fs-7'>
                          <span>{contact.displayName}</span>
                        </div>
                      </Link>
                    ) : (
                      <div className='lh-2 fs-7'>
                        <span className='fw-bolder mb-2'>{contact.displayName}</span>
                      </div>
                    )}

                    {data.primary && (
                      <i
                        title={`${
                          primaryLabel ?? intl.formatMessage({id: 'UTIL.PRIMARY_CONTACT'})
                        }`}
                        className={`mb-1 ms-3 fs-7 bi bi-star-fill ${
                          !data.primary ? 'text-gray-300' : 'text-primary'
                        }`}
                      />
                    )}
                  </div>
                </div>
                <div className='text-gray-600 fs-8'>{buildMainLine(data)}</div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  )
}
